/** Dark "living network" hero visual: enterprise systems around a glowing
 * Wise 2.0 core, glowing edges with light pulses converging inward and one
 * firing outward to a Live node. Pure SVG + SMIL (scales, no pixel drift).
 * Reduced-motion hides the pulses (.flow-dot) and freezes the rest. */

type Node = { x: number; y: number; label: string; color: string };

// viewBox 640x460, core at (320,230)
const CX = 320;
const CY = 230;

const NODES: Node[] = [
  { x: 96, y: 78, label: "Salesforce", color: "#38bdf8" },
  { x: 78, y: 210, label: "SAP", color: "#60a5fa" },
  { x: 108, y: 348, label: "PostgreSQL", color: "#818cf8" },
  { x: 300, y: 44, label: "NetSuite", color: "#22d3ee" },
  { x: 300, y: 416, label: "Slack", color: "#a78bfa" },
];

function edge(n: Node): string {
  // gentle curve from node toward the core
  const mx = (n.x + CX) / 2;
  const my = (n.y + CY) / 2 + (n.y < CY ? 18 : -18);
  return `M${n.x} ${n.y} Q ${mx} ${my} ${CX} ${CY}`;
}

const LIVE_PATH = `M${CX} ${CY} C 430 230, 470 150, 556 150`;

export function HeroCanvas() {
  return (
    <div className="relative">
      <svg viewBox="0 0 640 460" className="w-full" role="img" aria-label="Enterprise systems connecting into the Wise 2.0 AI core and going live">
        <defs>
          <radialGradient id="hc-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#a78bfa" />
            <stop offset="55%" stopColor="#6b30f5" />
            <stop offset="100%" stopColor="#22568f" />
          </radialGradient>
          <radialGradient id="hc-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#7b4dff" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#7b4dff" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="hc-edge" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#334155" />
            <stop offset="100%" stopColor="#7b4dff" />
          </linearGradient>
          <radialGradient id="hc-packet">
            <stop offset="0%" stopColor="#e9d5ff" />
            <stop offset="100%" stopColor="#7b4dff" />
          </radialGradient>
        </defs>

        {/* ambient core glow */}
        <circle cx={CX} cy={CY} r="150" fill="url(#hc-glow)" className="glow-breathe" style={{ transformOrigin: "320px 230px" }} />

        {/* edges */}
        {NODES.map((n) => (
          <path key={n.label} d={edge(n)} fill="none" stroke="url(#hc-edge)" strokeWidth="1.5" strokeOpacity="0.5" className="flow-path" />
        ))}
        <path d={LIVE_PATH} fill="none" stroke="#10b981" strokeWidth="1.5" strokeOpacity="0.6" className="flow-path" />

        {/* inbound light pulses */}
        {NODES.map((n, i) => (
          <circle key={`p-${n.label}`} className="flow-dot" r="3.6" fill="url(#hc-packet)">
            <animateMotion dur="2.6s" begin={`${i * 0.5}s`} repeatCount="indefinite" path={edge(n)} />
          </circle>
        ))}
        {/* outbound pulse to Live */}
        <circle className="flow-dot" r="3.6" fill="#34d399">
          <animateMotion dur="1.5s" begin="1.1s" repeatCount="indefinite" path={LIVE_PATH} />
        </circle>

        {/* source nodes */}
        {NODES.map((n) => {
          const w = n.label.length * 8.2 + 34;
          return (
            <g key={`n-${n.label}`} className="flow-node">
              <rect x={n.x - w / 2} y={n.y - 16} width={w} height="32" rx="16" fill="#0b1324" stroke="#1e293b" />
              <circle cx={n.x - w / 2 + 16} cy={n.y} r="4" fill={n.color} />
              <text x={n.x - w / 2 + 28} y={n.y + 4} fontSize="13" fontWeight="600" fill="#cbd5e1">
                {n.label}
              </text>
            </g>
          );
        })}

        {/* pulse rings + AI core */}
        <circle cx={CX} cy={CY} r="46" fill="none" stroke="#7b4dff" strokeOpacity="0.4" strokeWidth="1.5" className="hub-ring" />
        <circle cx={CX} cy={CY} r="46" fill="none" stroke="#38bdf8" strokeOpacity="0.4" strokeWidth="1.5" className="hub-ring" style={{ animationDelay: "1.5s" }} />
        <circle cx={CX} cy={CY} r="46" fill="url(#hc-core)" stroke="#a78bfa" strokeOpacity="0.5" />
        <path d={`M${CX} ${CY - 22}c2.4 10.9 4.9 13.4 15.8 15.8-10.9 2.4-13.4 4.9-15.8 15.8-2.4-10.9-4.9-13.4-15.8-15.8 10.9-2.4 13.4-4.9 15.8-15.8Z`} fill="#fff" />
        <text x={CX} y={CY + 74} fontSize="13" fontWeight="700" fill="#e2e8f0" textAnchor="middle">Wise 2.0 core</text>

        {/* live node */}
        <g>
          <rect x="556" y="132" width="76" height="36" rx="18" fill="#052e2b" stroke="#10b981" strokeOpacity="0.5" />
          <circle cx="576" cy="150" r="4" fill="#34d399" />
          <text x="588" y="155" fontSize="13" fontWeight="700" fill="#6ee7b7">Live</text>
        </g>
      </svg>

      {/* floating status toast */}
      <div className="chat-pop absolute -bottom-3 left-1/2 -translate-x-1/2 [animation-delay:900ms] sm:left-6 sm:translate-x-0">
        <div className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/10 px-4 py-2.5 text-sm text-white shadow-lift backdrop-blur-xl">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
          <span className="font-semibold">Salesforce connector</span>
          <span className="text-white/50">7/7 tests · signed · live</span>
        </div>
      </div>
    </div>
  );
}
